import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { get } from 'lodash-es';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { INCENTIVE_CATEGORY } from 'site-modules/shared/constants/incentives';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { buildMakeModelDefaultYear, VehicleDefaultModel } from 'client/data/models/vehicle-v2-default';
import { VisitorModel } from 'client/data/models/visitor';
import { getSupportedGraphqlModelYear } from './vehicle-model-year';

const DEFAULT = 'default';

export function buildVehiclePath({ make, model, year, submodel }) {
  return (
    make &&
    model &&
    `makes["${make}"].models["${model}"].years["${year || DEFAULT}"].submodels["${submodel || DEFAULT}"].vehicle`
  );
}

export function buildSubmodelsPath({ make, model, year }) {
  return make && model && year && `makes["${make}"].models["${model}"].years["${year}"].submodelsList`;
}

export function buildMostPopularStyleIdPath({ make, model, year }) {
  return make && model && year && `makes["${make}"].models["${model}"].years["${year}"].mostPopularStyleId`;
}

export function buildPricingInsightsPath(styleId) {
  return styleId && `styles["${styleId}"].pricingInsights`;
}

export const VehicleGraphqlEntities = {
  Vehicle: PropTypes.shape({
    id: PropTypes.number,
    make: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      id: PropTypes.number,
    }),
    model: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      modelLinkCode: PropTypes.string,
    }),
    submodels: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      adTargetId: PropTypes.string,
    }),
    defaultStyleId: PropTypes.number,
    styleIds: PropTypes.arrayOf(PropTypes.number),
    year: PropTypes.string,
    modelYearId: PropTypes.number,
    modelFamilyIds: PropTypes.arrayOf(PropTypes.string),
    pubStates: PropTypes.objectOf(PropTypes.bool),
    edmundsTypeCategories: PropTypes.objectOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    styleAttributes: PropTypes.shape({
      isElectric: PropTypes.bool,
      isPluginHybrid: PropTypes.bool,
      isTruck: PropTypes.bool,
    }),
  }),
  Submodel: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
};

export const VehicleGraphQLModel = createModelSegment('vehicleGraphql', [
  /**
   * @see buildVehiclePath
   */
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].submodels["{submodel}"].vehicle',
    async resolve({ make, model, submodel, year }, context) {
      const currentYear =
        year === DEFAULT
          ? await context.resolveValue(buildMakeModelDefaultYear({ make, model }), VehicleDefaultModel)
          : year;
      const modelYear = getSupportedGraphqlModelYear(currentYear);
      if (modelYear === null) {
        return null;
      }

      const extendedApiCache = context.getValue('extendedApiCache', FeatureFlagModel);
      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($make: String!, $model: String!, $submodel: String!, $year: Int!) {
              makes(makeSlug: $make) {
                models(modelSlug: $model) {
                  modelLinkCodes
                }
                id
              }
              modelYears(makeSlug: $make, modelSlug: $model, year: $year) {
                generations {
                  modelFamilyId
                }
                submodels(
                  sortBy: { shareBySales: DESC }
                  page: { pageSize: 1, pageNum: 1 }
                  filter: { slug: $submodel }
                ) {
                  styleList(filter: { lowestBaseMSRP: true }) {
                    styles {
                      id
                      engine {
                        fuelType
                      }
                      epaSpecifications {
                        electricityRange
                      }
                      bedLengthMeasurement
                    }
                  }
                  allStyles: styleList {
                    styles {
                      id
                    }
                  }
                  edmundsTypeCategories {
                    slug
                    id
                  }
                  internalName
                  slug
                  publicationStates
                  adTargetId
                  id
                }
                makeName
                makeSlug
                modelName
                modelSlug
                year
                id
              }
            }
          `,
          {
            make,
            model,
            year: modelYear,
            submodel: submodel === DEFAULT ? '' : submodel,
          },
          {
            useVenomCache: !!extendedApiCache,
          }
        )
        .then(response => {
          const pubStates = {};
          get(response, 'modelYears[0].submodels[0].publicationStates', []).forEach(pub => {
            pubStates[pub] = true;
          });

          const edmundsTypeCategories = {};
          get(response, 'modelYears[0].submodels[0].edmundsTypeCategories', []).forEach(category => {
            edmundsTypeCategories[category.slug] = { id: parseInt(category.id, 10), name: category.name };
          });

          const style = response.modelYears[0].submodels[0].styleList.styles[0];
          const styleIds = response.modelYears[0].submodels[0].allStyles.styles.map(({ id }) => parseInt(id, 10));

          return {
            id: parseInt(response.modelYears[0].id, 10),
            make: {
              name: response.modelYears[0].makeName,
              slug: response.modelYears[0].makeSlug,
              id: parseInt(response.makes[0].id, 10),
              // adTargetId
            },
            model: {
              name: response.modelYears[0].modelName,
              slug: response.modelYears[0].modelSlug,
              modelLinkCode: response.makes[0].models[0].modelLinkCodes[0],
              // adTargetId
            },
            submodels: {
              name: response.modelYears[0].submodels[0].internalName,
              slug: response.modelYears[0].submodels[0].slug,
              adTargetId: response.modelYears[0].submodels[0].adTargetId,
            },
            defaultStyleId: parseInt(style.id, 10), // lowest-priced
            styleIds,
            year: `${response.modelYears[0].year}`,
            modelYearId: parseInt(response.modelYears[0].id, 10),
            modelFamilyIds: response.modelYears[0].generations.map(({ modelFamilyId }) => modelFamilyId),
            pubStates,
            edmundsTypeCategories,
            styleAttributes: {
              isElectric: style.engine?.fuelType === 'electric',
              isPluginHybrid: style.engine?.fuelType !== 'electric' && !!style.epaSpecifications?.electricityRange,
              isTruck: !!style.bedLengthMeasurement,
            },
          };
        })
        .catch(() => null);
    },
  },
  /**
   * @see buildSubmodelsPath
   */
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].submodelsList',
    resolve({ make, model, year }, context) {
      const modelYear = getSupportedGraphqlModelYear(year);
      if (modelYear === null) {
        return Promise.resolve(null);
      }

      const extendedApiCache = context.getValue('extendedApiCache', FeatureFlagModel);

      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($make: String!, $model: String!, $year: Int!) {
              modelYears(makeSlug: $make, modelSlug: $model, year: $year) {
                submodels(filter: { excludeFleets: true }) {
                  internalName
                  name
                  slug
                }
              }
            }
          `,
          {
            make,
            model,
            year: modelYear,
          },
          {
            useVenomCache: !!extendedApiCache,
          }
        )
        .then(response => response.modelYears[0].submodels)
        .catch(() => null);
    },
  },
  /**
   * @see buildMostPopularStyleIdPath
   */
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].mostPopularStyleId',
    resolve({ make, model, year }, context) {
      const modelYear = getSupportedGraphqlModelYear(year);
      if (modelYear === null) {
        return Promise.resolve(null);
      }

      const extendedApiCache = context.getValue('extendedApiCache', FeatureFlagModel);

      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($make: String!, $model: String!, $year: Int!) {
              modelYears(makeSlug: $make, modelSlug: $model, year: $year) {
                styleList(page: { pageNum: 1, pageSize: 1 }, sortBy: { shareBySales: DESC }) {
                  styles {
                    id
                  }
                }
              }
            }
          `,
          {
            make,
            model,
            year: modelYear,
          },
          {
            useVenomCache: !!extendedApiCache,
          }
        )
        .then(response => response.modelYears[0].styleList.styles[0].id)
        .catch(() => null);
    },
  },
  /**
   * @see buildPricingInsightsPath
   */
  {
    path: 'styles["{styleId}"].pricingInsights',
    async resolve({ styleId }, context) {
      const zipCode = await context.resolveValue('location.zipCode', VisitorModel);
      const extendedApiCache = context.getValue('extendedApiCache', FeatureFlagModel);

      return withMetrics(EdmundsGraphQLFederation, context)
        .query(
          gql`
            query ($styleId: String!, $zipCode: String!, $input: NewTmvInput!) {
              style(styleId: $styleId) {
                newTmv(input: $input) {
                  totalWithOptions {
                    baseMSRP
                    tmv
                  }
                }
              }
              incentives(
                incentiveFilters: {
                  zip: $zipCode
                  styleIds: [$styleId]
                  inventoryCodes: [NEW]
                  types: { in: [CUSTOMER_CASH, CUSTOMER_CASH_AND_APR, CUSTOMER_APR, DEALER_CASH] }
                }
              ) {
                incentives {
                  rebateAmount
                  categories {
                    category
                  }
                  aprs(creditRatingTier: TIER1) {
                    apr
                  }
                  primary
                }
              }
            }
          `,
          {
            styleId: styleId.toString(),
            zipCode: zipCode.toString(),
            input: { zipcode: zipCode, optionSelection: { typical: true } },
          },
          { useVenomCache: !!extendedApiCache }
        )
        .then(({ style, incentives }) => {
          const { totalWithOptions } = style.newTmv;
          const styleIncentives = incentives.incentives;
          const primaryFinancingIncentive = styleIncentives.find(
            ({ categories, primary }) => primary && get(categories, '[0].category') === INCENTIVE_CATEGORY.FINANCING
          );

          return {
            totalMsrp: totalWithOptions.baseMSRP,
            tmv: totalWithOptions.tmv,
            cashRebates: styleIncentives
              .filter(({ categories }) => get(categories, '[0].category') === INCENTIVE_CATEGORY.REBATES)
              .reduce((total, { rebateAmount }) => total + (rebateAmount || 0), 0),
            apr: primaryFinancingIncentive?.aprs?.[0]?.apr,
          };
        })
        .catch(() => null);
    },
  },
]);
